import * as Dialog from '@radix-ui/react-dialog';
import { Loader2 } from 'lucide-react';
import { SubscriptionPackage } from '../../queries/subscriptionPackages';
import { StripePriceMappingDraft } from '../../queries/stripePriceMappings';

interface StripePriceMappingFormDialogProps {
  draft: StripePriceMappingDraft;
  onChange: (draft: StripePriceMappingDraft) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  error: string | null;
  packages: SubscriptionPackage[];
}

const inputClass = 'mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm';

export function StripePriceMappingFormDialog({
  draft,
  onChange,
  onCancel,
  onSave,
  saving,
  error,
  packages,
}: StripePriceMappingFormDialogProps) {
  const isEdit = !!draft.id;

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onCancel()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">{isEdit ? 'Edit mapping' : 'Add mapping'}</Dialog.Title>
          <p className="mt-1 text-sm text-muted-foreground">
            Manual DB edit only — this never creates or changes anything in Stripe. Use "Sync with Stripe" for that.
          </p>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <label className="text-sm">
              Package
              <select
                className={inputClass}
                value={draft.package_id ?? ''}
                onChange={(e) => onChange({ ...draft, package_id: e.target.value ? parseInt(e.target.value, 10) : undefined })}
              >
                <option value="">Select a package…</option>
                {packages.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              Billing cycle
              <select
                className={inputClass}
                value={draft.billing_cycle ?? 'weekly'}
                onChange={(e) => onChange({ ...draft, billing_cycle: e.target.value })}
              >
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </label>
            <label className="text-sm">
              Price amount ($)
              <input
                type="number"
                step="0.01"
                className={inputClass}
                value={draft.price_amount ?? ''}
                onChange={(e) => onChange({ ...draft, price_amount: e.target.value ? parseFloat(e.target.value) : undefined })}
              />
            </label>
            <label className="text-sm">
              Currency
              <input
                className={inputClass}
                value={draft.currency ?? 'aud'}
                onChange={(e) => onChange({ ...draft, currency: e.target.value })}
              />
            </label>
            <label className="col-span-2 text-sm">
              Stripe product ID (optional — set by Sync)
              <input
                className={`${inputClass} font-mono`}
                value={draft.stripe_product_id ?? ''}
                onChange={(e) => onChange({ ...draft, stripe_product_id: e.target.value })}
              />
            </label>
            <label className="col-span-2 text-sm">
              Stripe price ID (optional — set by Sync)
              <input
                className={`${inputClass} font-mono`}
                value={draft.stripe_price_id ?? ''}
                onChange={(e) => onChange({ ...draft, stripe_price_id: e.target.value })}
              />
            </label>
            {error && <p className="col-span-2 text-sm text-destructive">{error}</p>}
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
